import { Autocomplete, SxProps, createFilterOptions } from '@mui/material';
import { Component, SyntheticEvent } from 'react';
import { withLocale, WithLocaleProps } from '../../providers/LocaleProvider';
import DropIcon from '../../resources/images/icons-chevron-double.svg';
import { TextField } from '../TextField';

export interface OrganizationSelectorItem {
  organizationId: string;
  label: string;
}

interface OrganizationSelectorProps extends WithLocaleProps {
  value: OrganizationSelectorItem | null;
  onChange: (e: any) => void;
  items?: OrganizationSelectorItem[];
  sx?: SxProps;
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
}

// Controlled: the selected organization lives with the caller. It used to be mirrored in local state,
// which cannot work now that the option list arrives asynchronously — a value set after mount would
// never reach the input.
class OrganizationSelectorBase extends Component<OrganizationSelectorProps> {
  filterOptions: any;

  constructor(props: OrganizationSelectorProps) {
    super(props);

    this.filterOptions = createFilterOptions({
      matchFrom: 'any',
      stringify: (option: OrganizationSelectorItem) => option.label,
    });
  }

  onChange(e: SyntheticEvent, selected: OrganizationSelectorItem | null) {
    const { onChange } = this.props;
    if (!selected) return;

    onChange({ target: { value: selected } });
  }

  render() {
    const { sx, items, className, disabled, translate, value } = this.props;

    // Order (non-demo orgs first, demo orgs last) is set by the caller's sort.
    const options: OrganizationSelectorItem[] = items ?? [];

    return (
      // Generic params: single-select, non-clearable (org is required), no free-solo.
      // DisableClearable is typed as `boolean` so `value` remains nullable.
      <Autocomplete<OrganizationSelectorItem, false, boolean, false>
        className={'klayo-selector' + (className ? ' ' + className : '')}
        options={options}
        value={value}
        getOptionLabel={(option) => option.label ?? ''}
        // Match on id, never on label: two organizations can share a name.
        isOptionEqualToValue={(option, selected) =>
          option.organizationId === selected.organizationId
        }
        onChange={this.onChange.bind(this)}
        filterOptions={this.filterOptions}
        popupIcon={<DropIcon />}
        disableClearable
        handleHomeEndKeys
        disabled={disabled}
        multiple={false}
        renderInput={(params) => (
          <TextField
            {...params}
            label={translate('labels.organization')}
            placeholder={translate('common.select')}
            endAdornment={params.InputProps.endAdornment}
          />
        )}
        sx={sx}
        noOptionsText={translate('common.noOptions')}
      />
    );
  }
}

export const OrganizationSelector = withLocale(OrganizationSelectorBase);
